import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCommissions,
  fetchCommissionSummary,
  fetchMyCommissions,
  fetchCommissionsByRep,
  bulkPayCommissions,
  holdCommission,
  releaseCommission,
  overrideCommission,
  fetchPayouts,
} from '../lib/apiCommissions';
import type { CommissionFilters, BulkPayData } from '../types/commissions';

const KEYS = {
  commissions: 'commissions',
  summary: 'commissionSummary',
  myCommissions: 'myCommissions',
  byRep: 'commissionsByRep',
  payouts: 'commissionPayouts',
} as const;

export function useCommissions(filters: CommissionFilters = {}) {
  return useQuery({
    queryKey: [KEYS.commissions, filters],
    queryFn: () => fetchCommissions(filters),
  });
}

export function useCommissionSummary() {
  return useQuery({
    queryKey: [KEYS.summary],
    queryFn: () => fetchCommissionSummary(),
  });
}

export function useMyCommissions(status?: string) {
  return useQuery({
    queryKey: [KEYS.myCommissions, status],
    queryFn: () => fetchMyCommissions(status),
  });
}

export function useCommissionsByRep() {
  return useQuery({
    queryKey: [KEYS.byRep],
    queryFn: fetchCommissionsByRep,
  });
}

export function usePayoutHistory() {
  return useQuery({
    queryKey: [KEYS.payouts],
    queryFn: () => fetchPayouts(),
  });
}

function useInvalidateCommissions() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [KEYS.commissions] });
    queryClient.invalidateQueries({ queryKey: [KEYS.summary] });
    queryClient.invalidateQueries({ queryKey: [KEYS.myCommissions] });
    queryClient.invalidateQueries({ queryKey: [KEYS.byRep] });
    queryClient.invalidateQueries({ queryKey: [KEYS.payouts] });
  };
}

export function useBulkPayCommissions() {
  const invalidate = useInvalidateCommissions();
  return useMutation({
    mutationFn: (data: BulkPayData) => bulkPayCommissions(data),
    onSuccess: invalidate,
  });
}

export function useHoldCommission() {
  const invalidate = useInvalidateCommissions();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      holdCommission(id, reason),
    onSuccess: invalidate,
  });
}

export function useReleaseCommission() {
  const invalidate = useInvalidateCommissions();
  return useMutation({
    mutationFn: (id: number) => releaseCommission(id),
    onSuccess: invalidate,
  });
}

export function useOverrideCommission() {
  const invalidate = useInvalidateCommissions();
  return useMutation({
    mutationFn: ({ id, amount, reason }: { id: number; amount: number; reason: string }) =>
      overrideCommission(id, amount, reason),
    onSuccess: invalidate,
  });
}
